import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getSLADashboardOverview,
    getSLADashboardServices,
    getTicketSLASummary,
    getComplianceOverview,
    getComplianceByCustomers,
    getComplianceAtRiskServices,
    DashboardOverview,
    ServiceUptimeItem,
} from '@/modules/Service/services/sla'
import {
    ComplianceOverview,
    CustomerCompliance,
    ServiceAtRisk,
    TicketSLASummary,
} from '@/modules/Service/types/sla'

export interface DashboardData {
    // Overview
    overview: DashboardOverview | undefined
    isLoadingOverview: boolean
    // Services
    services: ServiceUptimeItem[]
    sortedServices: ServiceUptimeItem[]
    isLoadingServices: boolean
    // Ticket summary (realtime 60s)
    ticketSummary: TicketSLASummary | undefined
    isLoadingTicketSummary: boolean
    // Compliance
    complianceOverview: ComplianceOverview | undefined
    isLoadingCompliance: boolean
    // Customers
    customers: CustomerCompliance[]
    sortedCustomers: CustomerCompliance[]
    isLoadingCustomers: boolean
    // At-risk services
    atRiskServices: ServiceAtRisk[]
    isLoadingAtRisk: boolean
    // Derived
    compliancePercentRaw: number
}

export function useDashboardData(start: string, end: string): DashboardData {
    const { data: overviewData, isLoading: isLoadingOverview } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SERVICES_AVAILABLE_ADDONS, start, end],
            queryFn: () =>
                getSLADashboardOverview({ start_date: start, end_date: end }),
        })

    const { data: servicesData, isLoading: isLoadingServices } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.SERVICES_AVAILABLE_ADDONS,
                'list',
                start,
                end,
            ],
            queryFn: () =>
                getSLADashboardServices({ start_date: start, end_date: end }),
        })

    // Ticket summary — 60s refetch (realtime panel)
    const { data: ticketSummaryData, isLoading: isLoadingTicketSummary } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_TICKETS_SUMMARY],
            queryFn: getTicketSLASummary,
            refetchInterval: 60000,
        })

    const { data: complianceData, isLoading: isLoadingCompliance } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_COMPLIANCE_OVERVIEW, start, end],
            queryFn: () =>
                getComplianceOverview({ start_date: start, end_date: end }),
        })

    const { data: customersData, isLoading: isLoadingCustomers } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_COMPLIANCE_CUSTOMERS, start, end],
            queryFn: () =>
                getComplianceByCustomers({
                    start_date: start,
                    end_date: end,
                    limit: 20,
                    offset: 0,
                }),
        })

    const { data: atRiskData, isLoading: isLoadingAtRisk } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SLA_COMPLIANCE_AT_RISK],
            queryFn: () =>
                getComplianceAtRiskServices({ limit: 10, offset: 0 }),
        })

    const overview = overviewData?.result
    const services: ServiceUptimeItem[] = servicesData?.results || []
    const ticketSummary = ticketSummaryData?.result
    const complianceOverview = complianceData?.result
    const customers: CustomerCompliance[] = customersData?.results || []
    const atRiskServices: ServiceAtRisk[] = atRiskData?.results || []

    const sortedServices = useMemo(
        () =>
            [...services].sort((a, b) => {
                if (a.sla_met !== b.sla_met) return a.sla_met ? 1 : -1
                return a.actual_uptime - b.actual_uptime
            }),
        [services]
    )

    const sortedCustomers = useMemo(
        () =>
            [...customers].sort(
                (a, b) => a.avg_compliance_pct - b.avg_compliance_pct
            ),
        [customers]
    )

    // Raw float — never Math.round (UX review §Q9 correctness bug fix)
    const compliancePercentRaw = useMemo(() => {
        if (!overview || overview.total_services_with_sla === 0) return 0
        return (
            (overview.services_meeting_sla / overview.total_services_with_sla) *
            100
        )
    }, [overview])

    return {
        overview,
        isLoadingOverview,
        services,
        sortedServices,
        isLoadingServices,
        ticketSummary,
        isLoadingTicketSummary,
        complianceOverview,
        isLoadingCompliance,
        customers,
        sortedCustomers,
        isLoadingCustomers,
        atRiskServices,
        isLoadingAtRisk,
        compliancePercentRaw,
    }
}
